import { useTheme } from '@lifeomic/chroma-react/styles';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import {
  createContainer,
  Flyout,
  FlyoutProps,
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryClipContainer,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from 'victory';
import { ResultHistory } from '../../../../../../../services/MedicalResultsService';
import { Measurements, VictoryDatum } from './categoryGraphUtils';
import {
  GRAPH_CHART_PADDING,
  GRAPH_HEIGHT,
  LABEL_X_POSITION,
} from './constants';
import { getCondensedChartColors } from './chartThemeColors';
import { UnitPicker } from './UnitPicker';

const seriesColors = [
  '#9c78fe',
  '#017cf3',
  '#1eb253',
  '#febb26',
  '#ff8b00',
  '#ea3323',
];

const VictoryVoronoiZoomContainer = createContainer('voronoi', 'zoom') as any;

type GraphProps = {
  medicalResults: ResultHistory[];
  containerWidth: number;
};

export function CategoryGraph({ medicalResults, containerWidth }: GraphProps) {
  const theme = useTheme();
  const chartColors = useMemo(() => getCondensedChartColors(theme), [theme]);
  const biomarkerName = medicalResults[0]?.coding?.display ?? '';

  const measurements = useMemo(
    () => Measurements.fromObservations(medicalResults.map(r => r.resource)),
    [medicalResults],
  );

  const showUnitPicker = measurements.units.length > 1;
  const [selectedUnit, setSelectedUnit] = useState(measurements.units[0]);

  const measurementsByUnit = measurements.byUnit[selectedUnit];
  const { data: victoryData, ticks } = useMemo(
    () => measurementsByUnit.intoVictoryData(),
    [measurementsByUnit],
  );

  const shouldZoom = ticks.length > 10;
  const zoomDomain = {
    x: [Math.max(0, ticks.length - 10), ticks.length - 1] as [number, number],
  };

  const tickValues = useMemo(
    () => Array.from({ length: ticks.length }, (_, i) => i),
    [ticks.length],
  );

  const yTickValues = [
    measurementsByUnit.minValue.value,
    measurementsByUnit.maxValue.value,
  ];
  const yTickFormat = [
    measurementsByUnit.minValue.valueAsString,
    measurementsByUnit.maxValue.valueAsString,
  ];

  const latestValues = useMemo(() => {
    const maxToShow = 3;
    return victoryData.slice(0, maxToShow).map((data, index) => {
      const latest = data[data.length - 1];
      return { ...latest, color: seriesColors[index % seriesColors.length] };
    });
  }, [victoryData]);

  return (
    <div>
      {showUnitPicker && (
        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <UnitPicker
            units={measurements.units}
            selectedUnit={selectedUnit}
            onSelect={setSelectedUnit}
          />
        </div>
      )}
      <VictoryChart
        theme={{
          ...VictoryTheme.material,
          axis: {
            ...VictoryTheme.material.axis,
            style: {
              ...VictoryTheme.material.axis?.style,
              axis: { stroke: 'transparent' },
              grid: { stroke: 'transparent' },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: 'transparent' },
            },
          },
        }}
        padding={GRAPH_CHART_PADDING}
        containerComponent={
          <VictoryVoronoiZoomContainer
            style={{
              cursor: shouldZoom ? 'grab' : 'default',
            }}
            clipContainerComponent={
              <VictoryClipContainer
                clipPadding={{ top: 12, right: 12, left: 6, bottom: 12 }}
                translateX={38}
              />
            }
            zoomDomain={zoomDomain}
            allowZoom={false}
            allowPan
          />
        }
        minDomain={{ y: measurementsByUnit.minValue.value }}
        maxDomain={{ y: measurementsByUnit.maxValue.value }}
        height={showUnitPicker ? GRAPH_HEIGHT - 28 : GRAPH_HEIGHT}
        width={containerWidth}
      >
        <VictoryLabel
          x={0}
          y={9}
          text={
            biomarkerName.length > 80
              ? biomarkerName.slice(0, 80) + '…'
              : biomarkerName
          }
          style={{
            fill: chartColors.title,
            fontFamily: theme.typography.fontFamily,
            fontSize: 16,
            fontWeight: 'bold',
          }}
        />
        {latestValues.map((latest, index) => (
          <VictoryLabel
            key={`latest-value-${index}`}
            x={LABEL_X_POSITION(containerWidth)}
            y={14 + index * 30}
            lineHeight={1.6}
            text={[
              `${latest.asString}${latest.unit ? ` ${latest.unit}` : ''}`,
              truncateCategory(latest.category, 18),
            ]}
            style={[
              {
                fill: latest.color,
                fontFamily: theme.typography.fontFamily,
                fontSize: 13,
                fontWeight: 'bold',
                baselineShift: -4,
              },
              {
                fill: chartColors.labelMutedLight,
                fontFamily: theme.typography.fontFamily,
                fontSize: 10,
              },
            ]}
          />
        ))}
        {victoryData.length > 3 && (
          <VictoryLabel
            x={LABEL_X_POSITION(containerWidth)}
            y={14 + 3 * 30}
            text={`+${victoryData.length - 3} more`}
            style={{
              fill: chartColors.labelMutedMedium,
              fontFamily: theme.typography.fontFamily,
              fontSize: 11,
              fontStyle: 'italic',
            }}
          />
        )}
        <VictoryAxis
          style={{
            axis: { stroke: chartColors.axisStroke },
          }}
          tickLabelComponent={
            <VictoryLabel
              style={[
                {
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                  fill: chartColors.label,
                  textTransform: 'uppercase',
                },
                {
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                  fill: chartColors.label,
                  textTransform: 'uppercase',
                },
              ]}
              textAnchor="start"
              transform="translate(-10, 8)"
              lineHeight={1.2}
            />
          }
          tickValues={tickValues}
          tickFormat={(tick: number) => intoTickFormat(ticks, tick) as any}
        />
        {(['right', 'left'] as const).map(direction => (
          <VictoryAxis
            style={{
              grid: {
                stroke:
                  direction === 'left' ? chartColors.axisStroke : 'transparent',
                strokeWidth: 1,
              },
            }}
            key={`victory-axis-${direction}`}
            tickLabelComponent={
              <VictoryLabel
                dx={direction === 'left' ? -10 : 10}
                style={[
                  {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 12,
                    fill: chartColors.label,
                    display: direction === 'left' ? 'block' : 'none',
                  },
                  {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 12,
                    fill: chartColors.label,
                    display: direction === 'left' ? 'block' : 'none',
                  },
                ]}
              />
            }
            dependentAxis
            tickValues={yTickValues}
            tickFormat={yTickFormat}
            orientation={direction}
          />
        ))}
        <GradientDefs />
        {victoryData.map((data, index) => (
          <VictoryArea
            key={`victory-area-${index}`}
            data={data}
            style={{
              data: {
                fill: `url(#area-gradient-${index % seriesColors.length})`,
                stroke: 'transparent',
              },
            }}
          />
        ))}
        {victoryData.map((data, index) => (
          <VictoryLine
            key={`victory-line-${index}`}
            data={data}
            style={{
              data: {
                stroke: chartColors.lineStroke,
                strokeWidth: 2,
              },
            }}
          />
        ))}
        {victoryData.map((data, index) => (
          <VictoryScatter
            key={`victory-scatter-${index}`}
            data={data}
            labelComponent={
              <VictoryTooltip
                dx={10}
                style={[
                  {
                    fill: seriesColors[index % seriesColors.length],
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 20,
                    fontWeight: 'bold',
                  },
                  {
                    baselineShift: -4,
                    fill: chartColors.labelMuted,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 10,
                  },
                  {
                    baselineShift: -8,
                    fill: (props?: { datum?: VictoryDatum }) => {
                      if (!props?.datum?.delta) return chartColors.label;
                      return props.datum.delta > 0
                        ? chartColors.deltaPositive
                        : chartColors.deltaNegative;
                    },
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 14,
                  },
                  {
                    fontFamily: theme.typography.fontFamily,
                    baselineShift: -12,
                    fontSize: 12,
                  },
                ]}
                cornerRadius={4}
                flyoutStyle={{
                  fill: chartColors.flyoutFill,
                  stroke: 'transparent',
                }}
                flyoutPadding={{ top: 10, bottom: 10, left: 15, right: 15 }}
                textAnchor="start"
                orientation="right"
                flyoutComponent={<CustomFlyout />}
                constrainToVisibleArea
              />
            }
            labels={({ datum }: { datum: VictoryDatum }) => {
              const deltaLabel = formatDelta(datum.delta);
              return [datum.asString, datum.unit, deltaLabel, datum.category];
            }}
            size={({ active }) => (active ? 5 : 3)}
            style={{
              data: {
                fill: chartColors.scatterFill,
                stroke: seriesColors[index % seriesColors.length],
                strokeWidth: 2,
              },
            }}
          />
        ))}
      </VictoryChart>
    </div>
  );
}

function CustomFlyout(props: FlyoutProps) {
  // with VictoryVoronoiZoomContainer we occasionally get a flyout with an undefined x value
  // this prevents the app from crashing
  // tooltips remain unaffected
  if (props.x === undefined) {
    return null;
  }
  return <Flyout {...props} />;
}

function intoTickFormat(ticks: (DateTime | null)[], tick: number) {
  const currTick = ticks[tick];
  if (!currTick) return '';
  const monthAndDate = currTick.toFormat('d MMM');
  const year = currTick.toFormat('yyyy');
  return [monthAndDate, year];
}

function formatDelta(delta: number | null): string {
  if (!delta) return '';
  return delta > 0 ? `↑ ${delta}` : `↓ ${Math.abs(delta)}`;
}

function truncateCategory(category: string, maxLength: number): string {
  if (category.length <= maxLength) return category;
  return category.slice(0, maxLength - 1) + '…';
}

function GradientDefs() {
  return (
    <defs>
      {seriesColors.map((color, index) => (
        <linearGradient
          key={`gradient-${index}`}
          id={`area-gradient-${index}`}
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop offset="0%" stopColor={color} stopOpacity={0.15} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      ))}
    </defs>
  );
}
